import { AnswerSourceGroup } from "@/components/feature/AnswerSourceGroup/AnswerSourceGroup"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { mapApiCitationsToCitations } from "@/services/adapters/questionnaireAdapter/questionnaireAdapter"
import { Check, ChevronDown, ChevronUp, X } from "lucide-react"
import React, { useMemo, useState } from "react"
import { ProposalCard as ProposalCardType } from "../types"

interface ProposalCardProps {
  proposal: ProposalCardType
  onAccept: () => void
  onReject: () => void
  className?: string
}

/** Renders a single answer proposal with Accept / Reject controls. */
export const ProposalCard: React.FC<ProposalCardProps> = ({
  proposal,
  onAccept,
  onReject,
  className,
}) => {
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState(true)
  const { question, draftAnswer, status, citations } = proposal

  const isSettled = status === "accepted" || status === "rejected"
  const domainCitations = useMemo(
    () => mapApiCitationsToCitations(citations ?? []),
    [citations],
  )

  return (
    <div
      className={cn(
        "rounded-lg border text-sm transition-opacity",
        status === "pending" && "border-grey4 bg-grey2",
        status === "accepted" && "border-success/40 bg-success/10 opacity-70",
        status === "rejected" && "border-grey4 bg-grey3 opacity-50",
        className,
      )}
    >
      {/* Question header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-2 px-3 py-2 text-left"
      >
        <span className="flex-1 font-medium leading-snug text-grey6">
          {question}
        </span>
        {expanded ? (
          <ChevronUp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-grey6" />
        ) : (
          <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-grey6" />
        )}
      </button>

      {expanded && (
        <>
          {/* Draft answer */}
          <div className="border-t border-grey4 px-3 py-2">
            <p className="leading-relaxed text-grey5">
              {draftAnswer || (
                <span className="italic text-grey5">
                  {translate("AI_CHAT/PROPOSAL_NO_ANSWER")}
                </span>
              )}
            </p>
          </div>

          <div className="px-3">
            <AnswerSourceGroup citations={domainCitations} />
          </div>

          {/* Controls */}
          {!isSettled && (
            <div className="flex gap-2 border-t border-grey4 px-3 py-2">
              <button
                type="button"
                onClick={onAccept}
                className="flex items-center gap-1 rounded-md bg-success/20 px-3 py-1 text-xs font-medium text-success transition-colors hover:bg-success/30"
              >
                <Check className="h-3 w-3" />
                {translate("AI_CHAT/PROPOSAL_ACCEPT")}
              </button>
              <button
                type="button"
                onClick={onReject}
                className="flex items-center gap-1 rounded-md bg-grey4 px-3 py-1 text-xs font-medium text-grey6 transition-colors hover:bg-grey5"
              >
                <X className="h-3 w-3" />
                {translate("AI_CHAT/PROPOSAL_REJECT")}
              </button>
            </div>
          )}

          {status === "accepted" && (
            <div className="border-t border-grey4 px-3 py-2 text-xs text-success">
              {translate("AI_CHAT/PROPOSAL_SAVED")}
            </div>
          )}
          {status === "rejected" && (
            <div className="border-t border-grey4 px-3 py-2 text-xs text-grey5">
              {translate("AI_CHAT/PROPOSAL_DISMISSED")}
            </div>
          )}
        </>
      )}
    </div>
  )
}
